/**
 * ActionPlanDetailPage — Create / view / edit an Action Plan for a
 * published Summary Stock Audit Report.
 *
 * URL patterns:
 *   /summary-reports/:reportId/action-plan       → create or open existing AP
 *   /action-plans/:planId                        → open AP directly by ID
 *
 * Access:
 *   Internal users only (Auditor / Scheduler / Senior / Admin).
 *   Client-portal access is handled separately (deferred).
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  AlertCircle,
  ArrowLeft,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  CircleDot,
  Clock,
  Download,
  Loader2,
  Lock,
  Plus,
  Trash2,
  XCircle,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { actionPlansService, summaryReportsService } from '../services';
import { getApiError } from '@/lib/utils';
import { useAuth } from '../contexts/AuthContext';

// ─── Status helpers ──────────────────────────────────────────────────────────

const ITEM_STATUS_LABELS = {
  open:                   'Open',
  implemented:            'Implemented',
  partially_implemented:  'Partially Implemented',
  not_implemented:        'Not Implemented',
};

const ASSESSMENT_LABELS = {
  implemented:            'Implemented',
  carry_forward:          'Carry Forward',
  // Legacy values — read-only display only (cannot be newly set)
  partially_implemented:  'Partially Implemented (legacy)',
  not_implemented:        'Not Implemented (legacy)',
};

function statusBadge(status) {
  const map = {
    open:                  'bg-amber-100 text-amber-800 border-amber-200',
    implemented:           'bg-green-100 text-green-800 border-green-200',
    partially_implemented: 'bg-blue-100 text-blue-800 border-blue-200',
    not_implemented:       'bg-red-100 text-red-800 border-red-200',
    partially_resolved:    'bg-blue-100 text-blue-800 border-blue-200',
    resolved:              'bg-green-100 text-green-800 border-green-200',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${map[status] || 'bg-gray-100 text-gray-700 border-gray-200'}`}>
      {ITEM_STATUS_LABELS[status] || status}
    </span>
  );
}

function assessmentIcon(val) {
  if (!val) return null;
  if (val === 'implemented')
    return <CheckCircle2 className="w-4 h-4 text-green-600" />;
  if (val === 'carry_forward')
    return <ArrowLeft className="w-4 h-4 text-amber-600" />;
  if (val === 'partially_implemented')
    return <CircleDot className="w-4 h-4 text-blue-600" />;
  if (val === 'not_implemented')
    return <XCircle className="w-4 h-4 text-red-600" />;
  return null;
}

// ─── parseObsVariances ───────────────────────────────────────────────────────
// Mirrors the backend's _parse_obs_finding.  Extracts { label, rows[] } from
// an [Observations] finding_text block so we can render the source-variance
// table as a read-only UI element rather than raw pre-formatted text.
function parseObsVariances(finding_text) {
  if (!finding_text) return [];
  const body = finding_text.replace(/^\[Observations\][^\n]*\n?/, '').trim();
  if (!body) return [];
  const EM = ' \u2014 ';
  const sections = [];
  let current = null;
  for (const raw of body.split('\n')) {
    const line = raw.trim();
    if (!line) continue;
    if (!line.startsWith('\u2022') && line.endsWith(':')) {
      current = { label: line.slice(0, -1), rows: [] };
      sections.push(current);
      continue;
    }
    if (line.startsWith('\u2022') && current) {
      const core = line.slice(1).trim();
      let note = '';
      const noteMatch = core.match(/\s{2}\[([^\]]+)\]\s*$/);
      const cleanCore = noteMatch ? core.slice(0, core.length - noteMatch[0].length) : core;
      if (noteMatch) note = noteMatch[1];
      const di = cleanCore.indexOf(EM);
      if (di === -1) { current.rows.push({ item: cleanCore, amount: '', note }); continue; }
      const itemName = cleanCore.slice(0, di).trim();
      const detail   = cleanCore.slice(di + EM.length).trim();
      const amtMatch = detail.match(/=\s*([+\-][\u00a3$][\d,.]+)$/);
      current.rows.push({ item: itemName, amount: amtMatch ? amtMatch[1] : detail, note });
    }
  }
  return sections;
}

// ─── FindingsSection ─────────────────────────────────────────────────────────
// Editable "Findings / Contributing Factors" section rendered from the
// observation item's contributed_factors field.  Distinct from corrective
// actions — this is the team's explanation of WHY the result happened,
// not what they will DO about it.
function FindingsSection({ obsItem, onSave, saving }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue]     = useState(obsItem?.contributed_factors || '');

  if (!obsItem) return null;

  const handleSave = async () => {
    try {
      await onSave(obsItem.id, {
        finding_text:        null,   // source-frozen — must not be sent
        contributed_factors: value || null,
        status:              null,   // not independently resolvable on obs items
      });
      setEditing(false);
    } catch (e) {
      toast.error(getApiError(e));
    }
  };

  if (editing) {
    return (
      <div className="border border-blue-300 rounded-xl bg-white px-4 py-3 space-y-2" data-testid="ap-findings-section">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Findings</p>
        <textarea
          className="w-full border rounded px-3 py-2 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-blue-500"
          rows={4}
          placeholder="Describe contributing factors, root causes and context for this audit result…"
          value={value}
          onChange={e => setValue(e.target.value)}
          data-testid="ap-findings-textarea"
        />
        <div className="flex gap-2">
          <Button size="sm" onClick={handleSave} disabled={saving} data-testid="ap-findings-save-btn">
            {saving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null} Save
          </Button>
          <Button size="sm" variant="ghost" onClick={() => { setValue(obsItem?.contributed_factors || ''); setEditing(false); }}>Cancel</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="border rounded-xl bg-white px-4 py-3" data-testid="ap-findings-section">
      <div className="flex items-center justify-between mb-1.5">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Findings</p>
        <button
          className="text-xs text-blue-600 hover:text-blue-800 font-medium"
          onClick={() => { setValue(obsItem?.contributed_factors || ''); setEditing(true); }}
          data-testid="ap-findings-edit-btn"
        >
          Edit
        </button>
      </div>
      <p className={`text-sm leading-relaxed ${obsItem.contributed_factors ? 'text-gray-700' : 'text-gray-400 italic'}`}>
        {obsItem.contributed_factors || 'No findings recorded — click Edit to add the team\'s explanation of this result.'}
      </p>
    </div>
  );
}


function CorrectiveActionRow({ ca, planId, itemId, onUpdate, onDelete, saving, canWriteClientEntry }) {
  // Auto-open the assessment form for auditors when no verdict has been recorded
  // yet — removes one redundant click and surfaces the input prompt immediately.
  const needsAuditorAssessment = !canWriteClientEntry && !ca.auditor_assessment;
  const [editing, setEditing] = useState(needsAuditorAssessment);
  const [form, setForm] = useState({
    description:    ca.description,
    by_who:         ca.by_who || '',
    by_when:        ca.by_when || '',
    progress_note:  ca.progress_note || '',
    auditor_assessment: ca.auditor_assessment || '',
    auditor_assessment_note: ca.auditor_assessment_note || '',
  });

  useEffect(() => {
    if (!editing) return;
    setForm({
      description:        ca.description,
      by_who:             ca.by_who || '',
      by_when:            ca.by_when || '',
      progress_note:      ca.progress_note || '',
      auditor_assessment: ca.auditor_assessment || '',
      auditor_assessment_note: ca.auditor_assessment_note || '',
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing, ca.id]);

  const handleSave = async () => {
    try {
      // Build payload: only include client-entry fields if the user may write them.
      // Auditors (canWriteClientEntry=false) send only auditor_assessment fields;
      // the backend will also reject any client-entry fields from auditors.
      const payload = {
        auditor_assessment: form.auditor_assessment || null,
        auditor_assessment_note: form.auditor_assessment_note || null,
        version: ca.version,
      };
      if (canWriteClientEntry) {
        payload.description   = form.description;
        payload.by_who        = form.by_who || null;
        payload.by_when       = form.by_when || null;
        payload.progress_note = form.progress_note || null;
      }
      await onUpdate(itemId, ca.id, payload);
      setEditing(false);
    } catch (e) {
      toast.error(getApiError(e));
    }
  };

  const CLIENT_COMPLETION_LABELS = {
    in_progress:           'In Progress',
    implemented:           'Implemented',
    partially_implemented: 'Partially Implemented',
    not_implemented:       'Not Implemented',
  };
  const CLIENT_COMPLETION_COLORS = {
    in_progress:           'text-blue-700 bg-blue-50 border border-blue-200',
    implemented:           'text-green-700 bg-green-50 border border-green-200',
    partially_implemented: 'text-amber-700 bg-amber-50 border border-amber-200',
    not_implemented:       'text-red-700 bg-red-50 border border-red-200',
  };

  if (editing) {
    return (
      <div className="border rounded-lg p-3 bg-white space-y-3">
        {/* ── Client-entry fields ─────────────────────────────────────── */}
        {canWriteClientEntry ? (
          <>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Corrective Action *</label>
              <textarea
                className="w-full border rounded px-2 py-1.5 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-blue-500"
                rows={2}
                value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                data-testid={`ca-description-${ca.id}`}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">By Who</label>
                <input
                  type="text"
                  className="w-full border rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                  value={form.by_who}
                  onChange={e => setForm(f => ({ ...f, by_who: e.target.value }))}
                  data-testid={`ca-by-who-${ca.id}`}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">By When</label>
                <input
                  type="date"
                  className="w-full border rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                  value={form.by_when}
                  onChange={e => setForm(f => ({ ...f, by_when: e.target.value }))}
                  data-testid={`ca-by-when-${ca.id}`}
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Progress Note</label>
              <textarea
                className="w-full border rounded px-2 py-1.5 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-blue-500"
                rows={2}
                value={form.progress_note}
                onChange={e => setForm(f => ({ ...f, progress_note: e.target.value }))}
                data-testid={`ca-progress-${ca.id}`}
              />
            </div>
          </>
        ) : (
          /* Auditor view — client-entry fields shown read-only */
          <div className="rounded-md bg-slate-50 border border-slate-200 px-3 py-2.5 space-y-2"
               data-testid={`ca-client-entry-readonly-${ca.id}`}>
            <div className="flex items-center gap-1.5 mb-0.5">
              <Lock className="w-3 h-3 text-slate-400" />
              <span className="text-xs font-medium text-slate-500">Client-entry fields — read-only for auditors</span>
            </div>
            <div>
              <p className="text-[10px] text-slate-400 uppercase tracking-wide">Action</p>
              <p className="text-sm text-slate-700">{form.description || '—'}</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="text-[10px] text-slate-400 uppercase tracking-wide">By Who</p>
                <p className="text-sm text-slate-700">{form.by_who || '—'}</p>
              </div>
              <div>
                <p className="text-[10px] text-slate-400 uppercase tracking-wide">By When</p>
                <p className="text-sm text-slate-700">
                  {form.by_when ? new Date(form.by_when).toLocaleDateString('en-GB') : '—'}
                </p>
              </div>
            </div>
            {form.progress_note && (
              <div>
                <p className="text-[10px] text-slate-400 uppercase tracking-wide">Progress</p>
                <p className="text-sm text-slate-600 italic">{form.progress_note}</p>
              </div>
            )}
          </div>
        )}
        {/* ── Auditor-assessment fields (always editable for internal users) ── */}
        <div className={`rounded-lg p-3 space-y-2.5 ${!canWriteClientEntry && !form.auditor_assessment ? 'bg-amber-50/60 border border-amber-200' : 'bg-gray-50/40 border border-gray-200'}`}>
          {!canWriteClientEntry && !form.auditor_assessment && (
            <div className="flex items-center gap-1.5 pb-1 border-b border-amber-200/70">
              <AlertCircle className="w-3.5 h-3.5 text-amber-600 shrink-0" />
              <span className="text-xs font-semibold text-amber-700">Auditor assessment required</span>
            </div>
          )}
          <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Auditor Assessment</label>
          {/* Item 6: auditors may only set Implemented or Carry Forward; legacy values shown read-only */}
          {ca.auditor_assessment && !['implemented','carry_forward',''].includes(ca.auditor_assessment) ? (
            <div className="px-2 py-1.5 text-sm rounded border border-amber-200 bg-amber-50 text-amber-700 italic">
              {ASSESSMENT_LABELS[ca.auditor_assessment] || ca.auditor_assessment}
              <span className="ml-1 text-xs">(read-only — legacy status)</span>
            </div>
          ) : (
            <select
              className="border rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              value={form.auditor_assessment}
              onChange={e => setForm(f => ({
                ...f,
                auditor_assessment: e.target.value,
                auditor_assessment_note: '',
              }))}
              data-testid={`ca-assessment-${ca.id}`}
            >
              <option value="">— Not yet assessed —</option>
              <option value="implemented">Implemented</option>
              <option value="carry_forward">Carry Forward</option>
            </select>
          )}
          </div>
        {/* Carry Forward — optional note */}
        {form.auditor_assessment === 'carry_forward' && (
          <div>
            <label className="block text-xs font-medium text-amber-700 mb-1">
              Auditor comment (optional)
            </label>
            <textarea
              className="w-full border border-amber-200 rounded px-2 py-1.5 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-amber-400"
              rows={2}
              value={form.auditor_assessment_note}
              onChange={e => setForm(f => ({ ...f, auditor_assessment_note: e.target.value }))}
              placeholder="Reason for carrying forward (optional)…"
              data-testid={`ca-assessment-note-${ca.id}`}
            />
          </div>
        )}
        </div>
        <div className="flex gap-2">
          <Button size="sm" onClick={handleSave} disabled={saving} data-testid={`ca-save-${ca.id}`}>
            {saving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null} Save
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>Cancel</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="border rounded-lg p-3 bg-white">
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <p className="text-sm text-gray-800 whitespace-pre-wrap">{ca.description}</p>
          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1.5 text-xs text-gray-500">
            {ca.by_who && <span><span className="font-medium">By:</span> {ca.by_who}</span>}
            {ca.by_when && <span><span className="font-medium">When:</span> {new Date(ca.by_when).toLocaleDateString('en-GB')}</span>}
          </div>
          {ca.progress_note && (
            <p className="mt-1.5 text-xs text-gray-600 italic whitespace-pre-wrap">{ca.progress_note}</p>
          )}
          {ca.client_completion_status && (
            <div className="mt-1.5 flex items-center gap-2 flex-wrap">
              <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${CLIENT_COMPLETION_COLORS[ca.client_completion_status] || 'text-gray-600 bg-gray-100'}`}
                data-testid={`ca-client-completion-${ca.id}`}>
                Client: {CLIENT_COMPLETION_LABELS[ca.client_completion_status] || ca.client_completion_status}
              </span>
              {ca.client_completed_by_name && (
                <span className="text-xs text-gray-400">by {ca.client_completed_by_name}</span>
              )}
              {ca.completion_invalidated_at && (
                <span className="text-xs text-red-500 italic" data-testid={`ca-invalidated-internal-${ca.id}`}>
                  Invalidated by later edit
                </span>
              )}
            </div>
          )}
          {ca.auditor_assessment && (
            <div className="mt-1.5 flex items-center gap-1 flex-wrap">
              {assessmentIcon(ca.auditor_assessment)}
              <span className="text-xs font-medium text-gray-700">
                {ASSESSMENT_LABELS[ca.auditor_assessment]}
              </span>
              {ca.assessed_by_name && (
                <span className="text-xs text-gray-400">— assessed by {ca.assessed_by_name}</span>
              )}
              {ca.auditor_assessment_note && (
                <p className="w-full text-xs text-gray-500 italic mt-0.5">{ca.auditor_assessment_note}</p>
              )}
            </div>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button
            className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
            onClick={() => setEditing(true)}
            title="Edit"
            data-testid={`ca-edit-btn-${ca.id}`}
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </button>
          {canWriteClientEntry && (
            <button
              className="p-1 text-gray-400 hover:text-red-600 transition-colors"
              onClick={() => onDelete(itemId, ca.id)}
              title="Delete"
              data-testid={`ca-delete-btn-${ca.id}`}
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function FactorLinesSection({ item, planId, onAddFL, onUpdateFL, onDeleteFL, saving, canWriteClientEntry }) {
  const [addingFL, setAddingFL]   = useState(false);
  const [newFL, setNewFL]         = useState({ description: '', selling_price_value: '', quantity: '' });
  const [editingId, setEditingId] = useState(null);
  const [editFL, setEditFL]       = useState({});

  const fl_total = item.factor_lines?.reduce((sum, fl) => sum + (fl.line_total || 0), 0) || 0;

  const handleAdd = async () => {
    if (!newFL.description.trim()) { toast.error('Description required'); return; }
    try {
      await onAddFL(item.id, {
        description:         newFL.description,
        selling_price_value: newFL.selling_price_value ? parseFloat(newFL.selling_price_value) : null,
        quantity:            newFL.quantity ? parseFloat(newFL.quantity) : null,
      });
      setNewFL({ description: '', selling_price_value: '', quantity: '' });
      setAddingFL(false);
    } catch (e) { toast.error(getApiError(e)); }
  };

  const handleSaveEdit = async (fl) => {
    try {
      await onUpdateFL(item.id, fl.id, {
        description:         editFL.description,
        selling_price_value: editFL.selling_price_value ? parseFloat(editFL.selling_price_value) : null,
        quantity:            editFL.quantity ? parseFloat(editFL.quantity) : null,
        version:             fl.version,
      });
      setEditingId(null);
    } catch (e) { toast.error(getApiError(e)); }
  };

  return (
    <div className="mt-2">
      <div className="flex items-center justify-between mb-1">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
          Contributing Factor Lines {fl_total > 0 && <span className="text-gray-700 font-normal normal-case">(total: £{fl_total.toFixed(2)})</span>}
        </p>
        {canWriteClientEntry && (
          <button className="text-xs text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1" onClick={() => setAddingFL(v => !v)} data-testid={`add-fl-btn-${item.id}`}>
            <Plus className="w-3 h-3" /> Add Line
          </button>
        )}
      </div>

      {item.factor_lines?.map(fl => (
        editingId === fl.id ? (
          <div key={fl.id} className="border rounded p-2 mb-1 bg-white space-y-2">
            <input className="w-full border rounded px-2 py-1 text-xs" placeholder="Description" value={editFL.description || ''} onChange={e => setEditFL(f => ({...f, description: e.target.value}))} data-testid={`fl-desc-${fl.id}`} />
            <div className="flex gap-2">
              <div className="flex-1"><label className="text-xs text-gray-500">Selling Price (£)</label>
                <input type="number" step="0.01" className="w-full border rounded px-2 py-1 text-xs" value={editFL.selling_price_value || ''} onChange={e => setEditFL(f => ({...f, selling_price_value: e.target.value}))} data-testid={`fl-price-${fl.id}`} /></div>
              <div className="flex-1"><label className="text-xs text-gray-500">Quantity</label>
                <input type="number" step="0.01" className="w-full border rounded px-2 py-1 text-xs" value={editFL.quantity || ''} onChange={e => setEditFL(f => ({...f, quantity: e.target.value}))} data-testid={`fl-qty-${fl.id}`} /></div>
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={() => handleSaveEdit(fl)} disabled={saving} data-testid={`fl-save-${fl.id}`}>Save</Button>
              <Button size="sm" variant="ghost" onClick={() => setEditingId(null)}>Cancel</Button>
            </div>
          </div>
        ) : (
          <div key={fl.id} className="flex items-center justify-between text-xs bg-white border rounded px-3 py-1.5 mb-1">
            <span className="text-gray-700 flex-1">{fl.description}</span>
            <span className="text-gray-500 mx-3">{fl.selling_price_value != null ? `£${parseFloat(fl.selling_price_value).toFixed(2)}` : '—'} × {fl.quantity != null ? fl.quantity : '—'}</span>
            <span className="font-medium text-gray-900 mr-3">{fl.line_total != null ? `£${parseFloat(fl.line_total).toFixed(2)}` : '—'}</span>
            {canWriteClientEntry && (
              <div className="flex gap-1">
                <button className="text-gray-400 hover:text-blue-600" onClick={() => { setEditingId(fl.id); setEditFL({ description: fl.description, selling_price_value: fl.selling_price_value, quantity: fl.quantity }); }} data-testid={`fl-edit-${fl.id}`}>
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                </button>
                <button className="text-gray-400 hover:text-red-600" onClick={() => onDeleteFL(item.id, fl.id)} data-testid={`fl-delete-${fl.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            )}
          </div>
        )
      ))}

      {canWriteClientEntry && addingFL && (
        <div className="border rounded p-2 bg-blue-50 border-blue-200 space-y-2 mt-1">
          <input className="w-full border rounded px-2 py-1 text-xs" placeholder="Factor description (e.g. Draught beer variance)" value={newFL.description} onChange={e => setNewFL(f => ({...f, description: e.target.value}))} data-testid={`new-fl-desc-${item.id}`} />
          <div className="flex gap-2">
            <div className="flex-1">
              <label className="text-xs text-gray-500">Selling Price (£)</label>
              <input type="number" step="0.01" className="w-full border rounded px-2 py-1 text-xs" placeholder="0.00" value={newFL.selling_price_value} onChange={e => setNewFL(f => ({...f, selling_price_value: e.target.value}))} data-testid={`new-fl-price-${item.id}`} />
            </div>
            <div className="flex-1">
              <label className="text-xs text-gray-500">Quantity</label>
              <input type="number" step="0.01" className="w-full border rounded px-2 py-1 text-xs" placeholder="0" value={newFL.quantity} onChange={e => setNewFL(f => ({...f, quantity: e.target.value}))} data-testid={`new-fl-qty-${item.id}`} />
            </div>
            <div className="flex items-end">
              <span className="text-xs font-medium text-gray-700">
                = {newFL.selling_price_value && newFL.quantity ? `£${(parseFloat(newFL.selling_price_value) * parseFloat(newFL.quantity)).toFixed(2)}` : '—'}
              </span>
            </div>
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={handleAdd} disabled={saving} data-testid={`new-fl-save-${item.id}`}>Add</Button>
            <Button size="sm" variant="ghost" onClick={() => setAddingFL(false)}>Cancel</Button>
          </div>
        </div>
      )}
    </div>
  );
}

function ActionPlanItemRow({ item, planId, onUpdateItem, onDeleteItem, onAddCA, onUpdateCA, onDeleteCA, onAddFL, onUpdateFL, onDeleteFL, saving, canWriteClientEntry }) {
  // Observation items (seeded from report variance) are displayed as supporting
  // context, not independently-closable findings.  They still allow editing of
  // contributed_factors (the team's editable explanation context) and support
  // CAs/FLs, but the status badge and status select are hidden.
  const isObs = item.is_observation;

  const [expanded, setExpanded] = useState(true);
  const [editingItem, setEditingItem] = useState(false);
  const [itemForm, setItemForm] = useState({
    finding_text:        item.finding_text,
    contributed_factors: item.contributed_factors || '',
    status:              item.status,
  });
  // Item 4: New CA form open by default so client sees where to add actions immediately.
  const [addingCA, setAddingCA] = useState(true);
  const [newCA, setNewCA] = useState({ description: '', by_who: '', by_when: '', progress_note: '' });

  const handleSaveItem = async () => {
    try {
      await onUpdateItem(item.id, {
        finding_text:        isObs ? null : itemForm.finding_text,
        // contributed_factors is a client-entry field — omit it from the
        // payload when the user doesn't have write access (auditors).
        contributed_factors: canWriteClientEntry ? (itemForm.contributed_factors || null) : null,
        status:              isObs ? null : itemForm.status,
        version:             item.version,
      });
      setEditingItem(false);
    } catch (e) {
      toast.error(getApiError(e));
    }
  };

  const handleAddCA = async () => {
    if (!newCA.description.trim()) {
      toast.error('Corrective action description is required');
      return;
    }
    try {
      await onAddCA(item.id, {
        description:   newCA.description,
        by_who:        newCA.by_who || null,
        by_when:       newCA.by_when || null,
        progress_note: newCA.progress_note || null,
      });
      setNewCA({ description: '', by_who: '', by_when: '', progress_note: '' });
      setAddingCA(false);
    } catch (e) {
      toast.error(getApiError(e));
    }
  };

  return (
    <div className="border rounded-xl bg-white shadow-sm overflow-hidden" data-testid={`item-row-${item.id}`}>
      {/* Header — collapse toggle, parent Edit and parent Delete removed (Issue 318 legacy suppression).
          Items are always expanded; editing and deletion of the finding parent are internal-admin
          operations handled via the DB/admin tooling, not the AP review UI. */}
      <div className="flex items-start gap-3 p-4">
        <div className="flex-1 min-w-0">
          {editingItem ? (
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  {isObs ? 'Observation (source context — read from issued report)' : 'Context / Finding *'}
                </label>
                <textarea
                  className="w-full border rounded px-2 py-1.5 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-400"
                  rows={3}
                  value={itemForm.finding_text}
                  onChange={e => !isObs && setItemForm(f => ({ ...f, finding_text: e.target.value }))}
                  readOnly={isObs}
                  data-testid={`item-finding-${item.id}`}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  {isObs ? 'Contributing Context (team\'s explanation of this observation)' : 'Contributing Factors'}
                </label>
                {canWriteClientEntry ? (
                  <textarea
                    className="w-full border rounded px-2 py-1.5 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-blue-500"
                    rows={2}
                    placeholder={isObs ? 'Describe the team\'s explanation of these stock variances…' : 'Free text — describe the factors that contributed to this finding'}
                    value={itemForm.contributed_factors}
                    onChange={e => setItemForm(f => ({ ...f, contributed_factors: e.target.value }))}
                    data-testid={`item-factors-${item.id}`}
                  />
                ) : (
                  <div className="rounded-md bg-slate-50 border border-slate-200 px-3 py-2 flex items-start gap-1.5"
                       data-testid={`item-factors-readonly-${item.id}`}>
                    <Lock className="w-3 h-3 text-slate-400 mt-0.5 shrink-0" />
                    <p className="text-sm text-slate-600">
                      {itemForm.contributed_factors || <span className="italic text-slate-400">Not set — client-entry field, read-only for auditors</span>}
                    </p>
                  </div>
                )}
              </div>
              {!isObs && (
                <div className="flex items-end gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Status</label>
                    <select
                      className="border rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                      value={itemForm.status}
                      onChange={e => setItemForm(f => ({ ...f, status: e.target.value }))}
                      data-testid={`item-status-select-${item.id}`}
                    >
                      <option value="open">Open</option>
                      <option value="implemented">Implemented</option>
                      <option value="partially_implemented">Partially Implemented</option>
                      <option value="not_implemented">Not Implemented</option>
                    </select>
                  </div>
                </div>
              )}
              <div className="flex gap-2">
                <Button size="sm" onClick={handleSaveItem} disabled={saving} data-testid={`item-save-${item.id}`}>
                  {saving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null} Save
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setEditingItem(false)}>Cancel</Button>
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  {/* Issue 318: finding_text is a legacy heading for both obs and non-obs items.
                      Obs items: already surfaced by ap-source-variances-box.
                      Non-obs items: CA descriptions provide the context (matches client portal).
                      Neither is rendered here; contributed_factors and CAs give all needed detail. */}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                </div>
              </div>
              {item.contributed_factors && (
                <p className="mt-1 text-xs text-gray-500">
                  <span className="font-medium">{isObs ? 'Contributing context:' : 'Contributing factors:'}</span> {item.contributed_factors}
                </p>
              )}
              <div className="flex flex-wrap gap-x-4 gap-y-0.5 mt-1 text-xs text-gray-400">
                {(() => {
                  const today = new Date(); today.setHours(0, 0, 0, 0);
                  const overdue = (item.corrective_actions || []).filter(ca => {
                    if (!ca.by_when || ca.assessed_at) return false;
                    const d = new Date(ca.by_when); d.setHours(0, 0, 0, 0);
                    return d < today;
                  });
                  return overdue.length > 0 ? (
                    <span className="flex items-center gap-1 text-red-500" data-testid={`item-overdue-${item.id}`}>
                      <Clock className="w-3 h-3" />
                      {overdue.length} overdue corrective action{overdue.length !== 1 ? 's' : ''}
                    </span>
                  ) : null;
                })()}
                {item.carry_forward_from_stock_number && (
                  <span className="text-amber-600" data-testid={`cf-badge-${item.id}`}>
                    ↳ Carried forward from {item.carry_forward_from_stock_number}
                    {item.origin_item_id && item.origin_item_id !== item.carry_forward_from_id && (
                      <span className="ml-1 text-amber-400" title={`Original action ID: ${item.origin_item_id}`}>
                        (chain #{item.origin_item_id})
                      </span>
                    )}
                  </span>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Factor lines + Corrective actions section */}
      {expanded && (
        <div className="border-t bg-gray-50 px-4 py-3 space-y-2">

          {/* Factor Lines — only rendered when there are lines to show OR the current
              user can add lines.  Suppresses the empty header for read-only auditors. */}
          {((item.factor_lines?.length > 0) || canWriteClientEntry) && (
            <FactorLinesSection
              item={item}
              planId={planId}
              onAddFL={onAddFL}
              onUpdateFL={onUpdateFL}
              onDeleteFL={onDeleteFL}
              saving={saving}
              canWriteClientEntry={canWriteClientEntry}
            />
          )}

          {/* Divider — only when Factor Lines section rendered above */}
          {((item.factor_lines?.length > 0) || canWriteClientEntry) && (
            <div className="border-t border-gray-200 my-2" />
          )}

          {/* Corrective Actions — only rendered when there are CAs or the user can add.
              Suppresses the "Corrective Actions (0)" header for read-only auditors. */}
          {((item.corrective_actions?.length > 0) || canWriteClientEntry) && (
          <div>
          <div className="flex items-center justify-between mb-1">
            <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
              Corrective Actions ({item.corrective_actions?.length || 0})
            </p>
            {canWriteClientEntry && (
              <button
                className="text-xs text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1"
                onClick={() => setAddingCA(v => !v)}
                data-testid={`add-ca-btn-${item.id}`}
              >
                <Plus className="w-3 h-3" /> Add Action
              </button>
            )}
          </div>

          {/* Existing CAs */}
          {item.corrective_actions?.map(ca => (
            <CorrectiveActionRow
              key={ca.id}
              ca={ca}
              planId={planId}
              itemId={item.id}
              onUpdate={onUpdateCA}
              onDelete={onDeleteCA}
              saving={saving}
              canWriteClientEntry={canWriteClientEntry}
            />
          ))}

          {/* New CA form */}
          {addingCA && (
            <div className="border rounded-lg p-3 bg-white space-y-2 border-blue-200">
              <p className="text-xs font-semibold text-blue-700">New Corrective Action</p>
              <textarea
                className="w-full border rounded px-2 py-1.5 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-blue-500"
                rows={3}
                placeholder="Example: Additional stock counts will be carried out weekly by the Manager, and CCTV will be reviewed by end of this week. Retraining on security procedures scheduled for 1st of next month."
                value={newCA.description}
                onChange={e => setNewCA(f => ({ ...f, description: e.target.value }))}
                data-testid={`new-ca-description-${item.id}`}
              />
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs text-gray-600 mb-0.5">By Who</label>
                  <input
                    type="text"
                    className="w-full border rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                    value={newCA.by_who}
                    onChange={e => setNewCA(f => ({ ...f, by_who: e.target.value }))}
                    data-testid={`new-ca-by-who-${item.id}`}
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-0.5">By When</label>
                  <input
                    type="date"
                    className="w-full border rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                    value={newCA.by_when}
                    onChange={e => setNewCA(f => ({ ...f, by_when: e.target.value }))}
                    data-testid={`new-ca-by-when-${item.id}`}
                  />
                </div>
              </div>
              <textarea
                className="w-full border rounded px-2 py-1.5 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-blue-500"
                rows={2}
                placeholder="Progress note (optional)"
                value={newCA.progress_note}
                onChange={e => setNewCA(f => ({ ...f, progress_note: e.target.value }))}
                data-testid={`new-ca-progress-${item.id}`}
              />
              <div className="flex gap-2">
                <Button size="sm" onClick={handleAddCA} disabled={saving} data-testid={`new-ca-save-${item.id}`}>
                  {saving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null} Add
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setAddingCA(false)}>Cancel</Button>
              </div>
            </div>
          )}
        </div>
        )}
      </div>
      )}
    </div>
  );
}

// ─── Main page ───────────────────────────────────────────────────────────────

export default function ActionPlanDetailPage() {
  const { planId, reportId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAdmin, isScheduler, isAuditor } = useAuth();

  // Compute once — passed as prop so child components don't need their own auth calls.
  // Admin and (Senior) Scheduler have override write on client-entry AP fields;
  // pure Auditors are read-only on those fields.
  // View-as-Auditor: useAuth() already returns the auditor's effective User record
  // after startAuditorView() calls refreshUser(), so this check is transparent.
  // isScheduler is true for both scheduler AND senior_scheduler (see AuthContext.js:248).
  const canWriteClientEntry = isAdmin || isScheduler;

  const [plan, setPlan]           = useState(null);
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState(null);
  // ap_eligible for the parent report — used to gate the Create prompt when
  // navigated via /summary-reports/:reportId/action-plan and no plan exists yet.
  const [reportApEligible, setReportApEligible] = useState(null);

  // ── Draft AP publish flow (Items 2/9) ────────────────────────────────────
  const [showPublishModal, setShowPublishModal]       = useState(false);
  const [publishPreview, setPublishPreview]           = useState(null);
  const [publishPreviewLoading, setPublishPreviewLoading] = useState(false);
  const [publishing, setPublishing]                   = useState(false);

  // ── Prior AP review (Item 7) ─────────────────────────────────────────────
  const [priorReview, setPriorReview]             = useState(null);     // prior-review API data
  const [priorReviewExpanded, setPriorReviewExpanded] = useState(false);

  // Notification log panel (internal users only)
  const [apNotifLogs, setApNotifLogs]         = useState(null);
  const [notifExpanded, setNotifExpanded]     = useState(false);
  const [notifLoading, setNotifLoading]       = useState(false);

  // Delete plan (auditors only — before client input)
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteReason, setDeleteReason]       = useState('');
  const [deleting, setDeleting]               = useState(false);

  // Add new item
  const [addingItem, setAddingItem]   = useState(false);
  const [newItem, setNewItem]         = useState({
    finding_text: '', contributed_factors: '', display_order: 0,
  });

  const handleDeletePlan = async () => {
    if (deleteReason.trim().length < 10) return;
    setDeleting(true);
    try {
      await actionPlansService.deletePlan(plan.id, deleteReason.trim());
      toast.success('Action Plan deleted. The audit record has been retained.');
      setShowDeleteModal(false);
      navigate('/action-plans');
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(detail || getApiError(e));
    } finally {
      setDeleting(false);
    }
  };

  // ── Draft AP: load publish preview (Items 2/9) ───────────────────────────

  const handleOpenPublishModal = async () => {
    if (!plan?.id) return;
    setShowPublishModal(true);
    setPublishPreviewLoading(true);
    setPublishPreview(null);
    try {
      const data = await actionPlansService.getPublishPreview(plan.id);
      setPublishPreview(data);
    } catch (e) {
      toast.error('Could not load publish preview: ' + getApiError(e));
    } finally {
      setPublishPreviewLoading(false);
    }
  };

  const handlePublishDraft = async () => {
    if (!plan?.id) return;
    setPublishing(true);
    try {
      const res = await actionPlansService.publishDraft(plan.id);
      toast.success('Action Plan published.');
      if (res?.email_suppressed) {
        toast.warning(res.email_warning || 'No notification email was sent.', { duration: 10000 });
      } else if (res?.notified) {
        toast.info(`${res.notified} notification email(s) queued.`);
      }
      setShowPublishModal(false);
      setPublishPreview(null);
      loadPlan();
    } catch (e) {
      toast.error(getApiError(e));
    } finally {
      setPublishing(false);
    }
  };

  // ── Prior AP review: load on demand (Item 7) ─────────────────────────────

  const loadPriorReview = async () => {
    if (!plan?.id || !reportId) return;
    try {
      const data = await actionPlansService.getPriorReview({ report_id: parseInt(reportId, 10) });
      setPriorReview(data);
    } catch (_) {
      setPriorReview({ prior_ap: null, message: 'No prior AP found.' });
    }
    setPriorReviewExpanded(true);
  };

  const loadPlan = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (planId) {
        const data = await actionPlansService.get(planId);
        setPlan(data);
      } else if (reportId) {
        // Fetch report AP eligibility in parallel with existing AP lookup.
        // This lets us show an informational message (not a Create button)
        // when the client/group has AP disabled.
        const [list, reportResp] = await Promise.allSettled([
          actionPlansService.list({ report_id: reportId }),
          import('../services/api').then(m => m.default.get(`/summary-reports/${reportId}`)),
        ]);
        const eligible = reportResp.status === 'fulfilled'
          ? reportResp.value?.data?.ap_eligible ?? null
          : null;
        setReportApEligible(eligible);
        // List endpoint is paginated ({items: [...]}); prefer the exact plan id
        // passed by the AP archive so Edit opens the same plan as View.
        const rows = list.status === 'fulfilled'
          ? (Array.isArray(list.value) ? list.value : (list.value?.items || []))
          : [];
        const wantedId = parseInt(searchParams.get('plan') || '', 10);
        const target = rows.find(r => r.id === wantedId) || rows[0];
        if (target) {
          const full = await actionPlansService.get(target.id);
          setPlan(full);
        } else {
          // No plan yet — show create prompt or ineligible message
          setPlan(null);
        }
      }
    } catch (e) {
      setError(getApiError(e));
    } finally {
      setLoading(false);
    }
  }, [planId, reportId, searchParams]);

  useEffect(() => { loadPlan(); }, [loadPlan]);

  // Load notification log when panel is expanded (lazy — only once per plan)
  const loadNotifications = useCallback(async (id) => {
    if (!id) return;
    setNotifLoading(true);
    try {
      const logs = await actionPlansService.getNotifications(id);
      setApNotifLogs(logs);
    } catch (_) {
      setApNotifLogs([]);
    } finally {
      setNotifLoading(false);
    }
  }, []);

  const handleToggleNotifPanel = () => {
    const next = !notifExpanded;
    setNotifExpanded(next);
    if (next && apNotifLogs === null && plan?.id) {
      loadNotifications(plan.id);
    }
  };

  // ── Create plan ──────────────────────────────────────────────────────────

  const handleCreate = async () => {
    if (!reportId) return;
    setSaving(true);
    try {
      // Items 2/9: create as DRAFT — no notification is fired until auditor explicitly publishes.
      const data = await actionPlansService.create({
        report_id:     parseInt(reportId, 10),
        start_as_draft: true,
      });
      setPlan(data);
      toast.success('Action Plan created as draft. Review, then click Publish to send notifications.');
    } catch (e) {
      const msg = getApiError(e);
      toast.error(msg || 'Failed to create Action Plan — please refresh and try again');
    } finally {
      setSaving(false);
    }
  };

  // ── Add item ─────────────────────────────────────────────────────────────

  const handleAddItem = async () => {
    if (!newItem.finding_text.trim()) {
      toast.error('Finding text is required');
      return;
    }
    setSaving(true);
    try {
      const updated = await actionPlansService.addItem(plan.id, {
        finding_text:        newItem.finding_text,
        contributed_factors: newItem.contributed_factors || null,
        display_order:       newItem.display_order,
      });
      setPlan(updated);
      setNewItem({ finding_text: '', contributed_factors: '', display_order: 0 });
      setAddingItem(false);
      toast.success('Finding added');
    } catch (e) {
      toast.error(getApiError(e));
    } finally {
      setSaving(false);
    }
  };

  // ── Update item ──────────────────────────────────────────────────────────

  const handleUpdateItem = async (itemId, payload) => {
    setSaving(true);
    try {
      const updated = await actionPlansService.updateItem(plan.id, itemId, payload);
      setPlan(updated);
      toast.success('Finding updated');
    } catch (e) {
      throw e;
    } finally {
      setSaving(false);
    }
  };

  // ── Delete item ──────────────────────────────────────────────────────────

  const handleDeleteItem = async (itemId) => {
    if (!window.confirm('Remove this finding from the Action Plan?')) return;
    setSaving(true);
    try {
      const updated = await actionPlansService.deleteItem(plan.id, itemId);
      setPlan(updated);
      toast.success('Finding removed');
    } catch (e) {
      toast.error(getApiError(e));
    } finally {
      setSaving(false);
    }
  };

  // ── CA helpers ───────────────────────────────────────────────────────────

  const handleAddCA = async (itemId, payload) => {
    setSaving(true);
    try {
      const updated = await actionPlansService.addCorrectiveAction(plan.id, itemId, payload);
      setPlan(updated);
      toast.success('Corrective action added');
    } catch (e) {
      throw e;
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateCA = async (itemId, caId, payload) => {
    setSaving(true);
    try {
      const updated = await actionPlansService.updateCorrectiveAction(plan.id, itemId, caId, payload);
      setPlan(updated);
      toast.success('Corrective action updated');
    } catch (e) {
      throw e;
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCA = async (itemId, caId) => {
    if (!window.confirm('Remove this corrective action?')) return;
    setSaving(true);
    try {
      const updated = await actionPlansService.deleteCorrectiveAction(plan.id, itemId, caId);
      setPlan(updated);
    } catch (e) {
      toast.error(getApiError(e));
    } finally {
      setSaving(false);
    }
  };

  // ── Factor Lines ─────────────────────────────────────────────────────────

  const handleAddFL = async (itemId, payload) => {
    setSaving(true);
    try {
      const updated = await actionPlansService.addFactorLine(plan.id, itemId, payload);
      setPlan(updated);
      toast.success('Factor line added');
    } catch (e) { throw e; }
    finally { setSaving(false); }
  };

  const handleUpdateFL = async (itemId, flId, payload) => {
    setSaving(true);
    try {
      const updated = await actionPlansService.updateFactorLine(plan.id, itemId, flId, payload);
      setPlan(updated);
    } catch (e) { throw e; }
    finally { setSaving(false); }
  };

  const handleDeleteFL = async (itemId, flId) => {
    setSaving(true);
    try {
      const updated = await actionPlansService.deleteFactorLine(plan.id, itemId, flId);
      setPlan(updated);
    } catch (e) { toast.error(getApiError(e)); }
    finally { setSaving(false); }
  };

  // ── PDF Download ─────────────────────────────────────────────────────────

  const handleDownloadPdf = async () => {
    setSaving(true);
    try {
      const blob = await actionPlansService.downloadPdf(plan.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `action-plan-${(plan.site_name || plan.id).toString().replace(/\s+/g, '-')}-${plan.scope}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('PDF generation failed: ' + getApiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="flex items-center gap-2 text-red-600 mb-4">
          <AlertCircle className="w-5 h-5" />
          <p className="font-medium">{error}</p>
        </div>
        <Button variant="outline" onClick={() => navigate(-1)}>
          <ArrowLeft className="w-4 h-4 mr-2" /> Back
        </Button>
      </div>
    );
  }

  // No plan yet — create prompt or ineligible message
  if (!plan && reportId) {
    // AP is explicitly OFF for this client/group — do NOT offer Create.
    // Genuine existing plans (reached via /action-plans/:planId) are still
    // shown in full regardless of eligibility (read-only access preserved).
    if (reportApEligible === false) {
      return (
        <div className="max-w-3xl mx-auto px-4 py-8">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Report
          </Button>
          <div className="border rounded-xl p-8 text-center bg-white shadow-sm">
            <h2 className="text-xl font-semibold text-gray-800 mb-2">Action Plans Not Enabled</h2>
            <p className="text-gray-500 text-sm">
              Action Plans are not enabled for this customer. No Action Plan can be created for this report.
            </p>
          </div>
        </div>
      );
    }
    return (
      <div className="max-w-3xl mx-auto px-4 py-8">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="mb-4">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Report
        </Button>
        <div className="border rounded-xl p-8 text-center bg-white shadow-sm">
          <h2 className="text-xl font-semibold text-gray-800 mb-2">No Action Plan Exists</h2>
          <p className="text-gray-500 mb-6 text-sm">
            Create an Action Plan to record findings, contributing factors, corrective actions, and track progress.
          </p>
          <Button onClick={handleCreate} disabled={saving} data-testid="create-ap-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
            Create Action Plan
          </Button>
        </div>
      </div>
    );
  }

  if (!plan) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-8">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="mb-4">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back
        </Button>
        <p className="text-gray-500">Action Plan not found.</p>
      </div>
    );
  }

  return (
    <>
    <div className="max-w-4xl mx-auto px-4 py-6 space-y-5" data-testid="action-plan-detail">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(-1)}
          data-testid="ap-back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </Button>
      </div>

      {/* Items 2/9: Draft AP banner — shown until auditor explicitly publishes */}
      {plan.status === 'draft' && (
        <div className="bg-amber-50 border border-amber-300 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
          data-testid="ap-draft-banner">
          <div>
            <p className="text-sm font-semibold text-amber-800">Draft — Not Yet Published</p>
            <p className="text-xs text-amber-700 mt-0.5">
              This Action Plan is a draft. Notification emails will NOT be sent until you click Publish.
              Review the plan content below, then publish when ready.
            </p>
          </div>
          <Button
            size="sm"
            className="bg-amber-600 hover:bg-amber-700 text-white shrink-0"
            onClick={handleOpenPublishModal}
            disabled={publishing || publishPreviewLoading}
            data-testid="ap-publish-btn"
          >
            {(publishing || publishPreviewLoading) ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
            Publish &amp; Notify
          </Button>
        </div>
      )}

      {/* Item 7: Prior AP review panel — load on demand */}
      {reportId && plan.status === 'draft' && (
        <div className="border rounded-xl bg-white shadow-sm overflow-hidden" data-testid="prior-ap-review-section">
          <button
            className="w-full flex items-center justify-between px-5 py-3 text-left hover:bg-gray-50 transition-colors"
            onClick={() => {
              if (!priorReview) loadPriorReview();
              else setPriorReviewExpanded(v => !v);
            }}
            data-testid="prior-ap-review-toggle"
          >
            <div>
              <span className="text-sm font-semibold text-gray-800">Prior AP Review</span>
              <span className="ml-2 text-xs text-gray-500">
                Review open items from the previous Action Plan before publishing
              </span>
            </div>
            {priorReviewExpanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
          </button>
          {priorReviewExpanded && priorReview && (
            <div className="px-5 pb-4 border-t">
              {!priorReview.prior_ap ? (
                <p className="text-sm text-gray-500 mt-3 italic">{priorReview.message}</p>
              ) : (
                <div className="mt-3 space-y-3">
                  <p className="text-xs text-gray-500">
                    Prior AP #{priorReview.prior_ap.plan_id} · {priorReview.prior_ap.audit_date} ·{' '}
                    <span className="font-medium capitalize">{priorReview.prior_ap.status}</span>
                    {' · '}{priorReview.open_item_count} open item(s)
                  </p>
                  {(priorReview.open_items || []).map(item => (
                    <div key={item.item_id} className="border rounded-lg p-3 bg-gray-50 text-sm">
                      <p className="font-medium text-gray-800 mb-2">{item.finding_text}</p>
                      {(item.corrective_actions || []).map(ca => (
                        <PriorCaReviewRow
                          key={ca.ca_id}
                          ca={ca}
                          planId={priorReview.prior_ap.plan_id}
                          itemId={item.item_id}
                          onSaved={() => loadPriorReview()}
                        />
                      ))}
                    </div>
                  ))}
                  <p className="text-xs text-gray-400 italic">
                    After recording verdicts, eligible unresolved items will be carried forward automatically when you publish.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <div className="bg-white border rounded-xl p-5 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <h1 className="text-xl font-semibold text-gray-900">Action Plan</h1>
            </div>
            {plan.site_name && (
              <p className="text-sm text-gray-700 font-medium">
                {plan.site_name}
              </p>
            )}
            {(plan.scope || plan.audit_date || (plan.trigger_type === 'manual' && plan.triggered_by_name)) && (
              <p className="text-xs text-gray-400 mt-0.5">
                {plan.scope || ''}
                {plan.audit_date && `${plan.scope ? ' \u00b7 ' : ''}${new Date(plan.audit_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}`}
                {plan.trigger_type === 'manual' && plan.triggered_by_name && ` \u00b7 Manual \u00b7 ${plan.triggered_by_name}`}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownloadPdf}
              disabled={saving}
              className="text-gray-600"
              data-testid="ap-download-pdf-btn"
            >
              <Download className="w-4 h-4 mr-1" /> PDF
            </Button>
            {isAuditor && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowDeleteModal(true)}
                className="text-red-600 border-red-200 hover:bg-red-50"
                data-testid="ap-delete-plan-btn"
              >
                <Trash2 className="w-4 h-4 mr-1" /> Delete Plan
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Result snapshot — prominent period result + secondary metrics grid */}
      {(() => {
        const snap = plan.result_snapshot || {};
        const rc  = snap.result_cash;
        const rp  = snap.result_pct ?? plan.source_result_value;
        const yc  = snap.ytd_cash;
        const yp  = snap.ytd_pct;
        const sh  = snap.stockholding;
        const yld = snap.yield_pct;
        const ytdYield = snap.ytd_yield_pct;
        const periodDays = plan.stock_period_days;
        const prevAudit  = plan.previous_audit_date;
        const fmtD = d => {
          if (!d) return null;
          const [y, m, dy] = d.slice(0, 10).split('-').map(Number);
          return new Date(y, m - 1, dy).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
        };
        const periodLabel = periodDays == null ? '\u2014'
          : (prevAudit && plan.audit_date
              ? `${fmtD(prevAudit)} \u2013 ${fmtD(plan.audit_date)} (${periodDays}d)`
              : `${periodDays} days`);

        const fmtCash = v => v == null ? '\u2014' : `${v < 0 ? '-' : '+'}£${Math.abs(v).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
        const fmtAbs  = v => v == null ? '\u2014' : `£${Math.abs(v).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
        const fmtPct  = v => v == null ? '\u2014' : `${v > 0 ? '+' : ''}${Number(v).toFixed(2)}%`;

        const allFL = (plan.items || []).flatMap(it => it.factor_lines || []).filter(fl => fl.line_total != null);
        const flTotal = allFL.length ? allFL.reduce((s, fl) => s + fl.line_total, 0) : null;
        const unexplained = rc != null && rc < 0 && flTotal != null ? rc + flTotal : null;

        const isNeg = rc != null ? rc < 0 : rp != null ? rp < 0 : null;
        const primaryColor = isNeg === true ? 'text-red-600' : isNeg === false ? 'text-green-600' : 'text-gray-800';
        const ytdColor = yc != null && yc < 0 ? 'text-red-600' : yc != null && yc > 0 ? 'text-green-600' : 'text-gray-700';

        // Threshold context — values now included in result_snapshot (from ActionPlanConfig via _load_plan_full)
        const tLow    = snap.threshold_low  != null ? Number(snap.threshold_low)  : null;
        const tHigh   = snap.threshold_high != null ? Number(snap.threshold_high) : null;
        const excessPp = snap.threshold_excess_pp != null ? Number(snap.threshold_excess_pp) : null;
        const breachDir = snap.breach_direction || null;
        const hasThreshold = tLow != null || tHigh != null;
        const isBreach = breachDir != null && excessPp != null;
        const thresholdLabel = hasThreshold
          ? [tLow != null ? `Low ${fmtPct(tLow)}` : null, tHigh != null ? `High ${fmtPct(tHigh)}` : null].filter(Boolean).join(' / ')
          : null;

        return (
          <div className="bg-white border rounded-xl shadow-sm overflow-hidden" data-testid="ap-result-snapshot">
            {/* Primary row — period result + YTD */}
            <div className="grid grid-cols-2 divide-x border-b">
              <div className="px-5 py-4 text-center">
                <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest mb-1">Period Result</p>
                <p className={`text-2xl font-bold tabular-nums leading-none ${primaryColor}`}>{fmtPct(rp)}</p>
                <p className={`text-sm font-medium mt-1 ${primaryColor}`}>{fmtCash(rc)}</p>
              </div>
              <div className="px-5 py-4 text-center">
                <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest mb-1">YTD</p>
                <p className={`text-2xl font-bold tabular-nums leading-none ${ytdColor}`}>{fmtPct(yp)}</p>
                <p className={`text-sm font-medium mt-1 ${ytdColor}`}>{fmtCash(yc)}</p>
              </div>
            </div>
            {/* Secondary metrics row */}
            <div className="grid grid-cols-2 sm:grid-cols-4 divide-x bg-gray-50/60">
              <div className="px-4 py-2.5 text-center">
                <p className="text-[10px] text-gray-400 uppercase tracking-wide">Stockholding</p>
                <p className="text-base font-semibold text-gray-700 mt-0.5">{fmtAbs(sh)}</p>
              </div>
              <div className="px-4 py-2.5 text-center">
                <p className="text-[10px] text-gray-400 uppercase tracking-wide">Yield</p>
                <p className="text-base font-semibold text-gray-700 mt-0.5">{fmtPct(yld)}</p>
              </div>
              <div className="px-4 py-2.5 text-center">
                <p className="text-[10px] text-gray-400 uppercase tracking-wide">YTD Yield</p>
                <p className="text-base font-semibold text-gray-700 mt-0.5" data-testid="ap-ytd-yield">{fmtPct(ytdYield)}</p>
              </div>
              <div className="px-4 py-2.5 text-center">
                <p className="text-[10px] text-gray-400 uppercase tracking-wide">Period</p>
                <p className="text-base font-semibold text-gray-700 mt-0.5 leading-tight" data-testid="ap-period-days">{periodLabel}</p>
              </div>
            </div>
            {/* Factor analysis row — only when factor lines are present */}
            {(flTotal != null || unexplained != null) && (
              <div className="grid grid-cols-2 divide-x border-t bg-amber-50/40">
                {flTotal != null && (
                  <div className="px-4 py-2.5 text-center">
                    <p className="text-[10px] text-gray-400 uppercase tracking-wide">Explained</p>
                    <p className="text-xs font-semibold text-gray-700 mt-0.5">£{flTotal.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                  </div>
                )}
                {unexplained != null && (
                  <div className="px-4 py-2.5 text-center">
                    <p className="text-[10px] text-gray-400 uppercase tracking-wide">Unexplained Balance</p>
                    <p className={`text-xs font-semibold mt-0.5 ${unexplained < 0 ? 'text-red-600' : 'text-green-600'}`}>{fmtCash(unexplained)}</p>
                  </div>
                )}
              </div>
            )}
            {/* Threshold / breach explanation row — shown when config is set; mirrors PDF section */}
            {hasThreshold && (
              <div className={`border-t px-5 py-2.5 flex items-center gap-3 flex-wrap ${isBreach ? 'bg-red-50/50' : 'bg-gray-50/40'}`}
                   data-testid="ap-threshold-row">
                <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest shrink-0">Threshold</span>
                <span className="text-xs text-gray-700 font-medium">{thresholdLabel}</span>
                {isBreach ? (
                  <span className="text-xs font-semibold text-red-700 bg-red-100 px-2 py-0.5 rounded-full border border-red-200" data-testid="ap-threshold-breach-badge">
                    {Number(excessPp).toFixed(2)}% {breachDir} threshold
                  </span>
                ) : (
                  <span className="text-xs text-green-700 bg-green-50 px-2 py-0.5 rounded-full border border-green-200" data-testid="ap-threshold-within-badge">
                    Within threshold
                  </span>
                )}
              </div>
            )}
          </div>
        );
      })()}

            {/* Source Variances — read-only box from the observation item's finding_text.
          Shows "Top Shortages" for a negative period result, "Top Surpluses" for
          positive.  Parsed client-side using parseObsVariances (mirrors backend
          _parse_obs_finding).  Not editable — source figures must not be altered
          after report publication.
          Note: contributed_factors (Findings narrative) is preserved in the DB
          for history/PDF but is intentionally not surfaced in this UI view. */}
      {(() => {
        const obsItem = (plan.items || []).find(i => i.is_observation);
        if (!obsItem) return null;
        const srcResult  = plan.source_result_value;
        const targetLbl  = srcResult != null
          ? (srcResult < 0 ? 'Shortages' : srcResult > 0 ? 'Surpluses' : null)
          : null;
        const allSecs    = parseObsVariances(obsItem.finding_text);
        const matched    = targetLbl ? allSecs.filter(s => s.label === targetLbl) : allSecs;
        const isNeg      = targetLbl === 'Shortages';
        const headerBg   = isNeg ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200';
        const headerText = isNeg ? 'text-red-700' : 'text-green-700';
        const borderCls  = isNeg ? 'border-red-200' : 'border-green-200';
        if (!matched.length) return null;
        return (
          <div className={`border rounded-xl overflow-hidden ${borderCls}`} data-testid="ap-source-variances-box">
            <div className={`px-4 py-2.5 flex items-center gap-2.5 border-b ${headerBg}`}>
              <span className={`text-xs font-bold uppercase tracking-wider ${headerText}`}>
                Top {targetLbl}
              </span>
              <span className="text-xs text-gray-400 italic">source data — read only</span>
            </div>
            <table className="w-full text-sm border-collapse bg-white">
              <thead>
                <tr className="border-b bg-gray-50">
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Product</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 w-24">Variance</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-400">Auditor Note</th>
                </tr>
              </thead>
              <tbody>
                {matched[0].rows.map((row, i) => (
                  <tr key={i} className="border-b last:border-0 hover:bg-gray-50/40">
                    <td className="px-4 py-2 text-gray-800">{row.item}</td>
                    <td className={`px-4 py-2 text-right font-semibold tabular-nums whitespace-nowrap ${
                      row.amount.startsWith('-') ? 'text-red-600'
                      : row.amount.startsWith('+') ? 'text-green-600'
                      : 'text-gray-700'
                    }`}>
                      {row.amount || '\u2014'}
                    </td>
                    <td className="px-4 py-2 text-xs text-gray-400 italic">{row.note || ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })()}

      {/* Items — AP Package 4 plan-level submission metadata (read-only in internal portal) */}
      {plan.plan_submission_status === 'submitted' && (
        <div
          className="flex flex-col sm:flex-row sm:items-center gap-3 bg-blue-50 border border-blue-200 rounded-xl px-4 py-3"
          data-testid="ap-plan-submission-info"
        >
          <span
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-blue-700 bg-blue-100 border border-blue-300 px-2.5 py-1 rounded-full shrink-0"
            data-testid="ap-plan-submitted-badge"
          >
            Plan Submitted
          </span>
          <span className="text-xs text-blue-600">
            {plan.plan_submitted_by_name
              ? `by ${plan.plan_submitted_by_name}`
              : ''}
            {plan.plan_submitted_at
              ? ` on ${new Date(plan.plan_submitted_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}`
              : ''}
            {plan.plan_submission_revision
              ? ` · Revision ${plan.plan_submission_revision}`
              : ''}
          </span>
          <span className="text-xs text-blue-400 sm:ml-auto italic">
            Read-only — submitted by client. Distinct from per-action completion and auditor verification.
          </span>
        </div>
      )}

      {/* Items — Issue 318: same visibility filter as ClientActionPlanPage.
          Observation items: always shown (they carry the variance summary).
          Non-observation items: shown only when they have client-entered content
          (CAs, factor lines, holistic_response, or contributed_factors).
          "Permission test finding" and other empty placeholder items are hidden. */}
      <div className="space-y-3">
        {/* Empty state */}
        {(plan.items || []).filter(i =>
          i.is_observation ||
          (i.corrective_actions?.length > 0) ||
          (i.factor_lines?.length > 0) ||
          i.holistic_response ||
          i.contributed_factors
        ).length === 0 && (
          <div className="border rounded-xl p-6 text-center bg-white text-gray-400 text-sm" data-testid="ap-no-items-placeholder">
            No action items recorded.
          </div>
        )}

        {(plan.items || [])
          .filter(i =>
            i.is_observation ||
            (i.corrective_actions?.length > 0) ||
            (i.factor_lines?.length > 0) ||
            i.holistic_response ||
            i.contributed_factors
          )
          .map(item => (
            <ActionPlanItemRow
              key={item.id}
              item={item}
              planId={plan.id}
              onUpdateItem={handleUpdateItem}
              onDeleteItem={handleDeleteItem}
              onAddCA={handleAddCA}
              onUpdateCA={handleUpdateCA}
              onDeleteCA={handleDeleteCA}
              onAddFL={handleAddFL}
              onUpdateFL={handleUpdateFL}
              onDeleteFL={handleDeleteFL}
              saving={saving}
              canWriteClientEntry={canWriteClientEntry}
            />
          ))}

        {/* AP Notification Log — internal users only */}
        <div className="border rounded-xl bg-white shadow-sm overflow-hidden" data-testid="ap-notif-log-panel">
          <button
            className="w-full flex items-center justify-between px-5 py-3 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
            onClick={handleToggleNotifPanel}
            data-testid="ap-notif-log-toggle"
          >
            <span className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-slate-400" />
              Notification Log
              {apNotifLogs !== null && (
                <span className="ml-1 text-xs font-normal text-slate-400">
                  ({apNotifLogs.length} {apNotifLogs.length === 1 ? 'row' : 'rows'})
                </span>
              )}
            </span>
            {notifExpanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
          </button>

          {notifExpanded && (
            <div className="border-t px-5 py-4">
              {notifLoading ? (
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <Loader2 className="w-4 h-4 animate-spin" /> Loading…
                </div>
              ) : apNotifLogs?.length === 0 ? (
                <p className="text-sm text-slate-400 italic">No notification rows staged for this plan yet.</p>
              ) : apNotifLogs ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs border-collapse" data-testid="ap-notif-log-table">
                    <thead>
                      <tr className="border-b text-left text-slate-500">
                        <th className="pb-2 pr-3 font-medium">Event</th>
                        <th className="pb-2 pr-3 font-medium">Recipient</th>
                        <th className="pb-2 pr-3 font-medium">Status</th>
                        <th className="pb-2 pr-3 font-medium">Subject</th>
                        <th className="pb-2 font-medium">Staged</th>
                      </tr>
                    </thead>
                    <tbody>
                      {apNotifLogs.map(log => {
                        const statusColour = {
                          sent:          'text-green-700 bg-green-50 border-green-200',
                          queued:        'text-blue-700 bg-blue-50 border-blue-200',
                          disabled:      'text-slate-500 bg-slate-50 border-slate-200',
                          suppressed_org:'text-amber-700 bg-amber-50 border-amber-200',
                          failed:        'text-red-700 bg-red-50 border-red-200',
                          captured:      'text-purple-700 bg-purple-50 border-purple-200',
                          dedup_skipped: 'text-slate-400 bg-slate-50 border-slate-200',
                        }[log.status] || 'text-slate-500 bg-slate-50 border-slate-200';
                        const statusLabel = {
                          sent:           'Sent',
                          queued:         'Ready to send',
                          disabled:       'Preview — not sent',
                          suppressed_org: 'Held by org setting',
                          failed:         'Failed',
                          captured:       'Staged',
                          dedup_skipped:  'Duplicate — skipped',
                        }[log.status] || log.status;
                        const eventShort = (log.event_type || '').replace('AP_', '').replace(/_/g, ' ');
                        return (
                          <tr key={log.id} className="border-b last:border-0 hover:bg-slate-50">
                            <td className="py-2 pr-3 font-mono text-slate-600 whitespace-nowrap">{eventShort}</td>
                            <td className="py-2 pr-3 text-slate-700">{log.recipient_email}</td>
                            <td className="py-2 pr-3">
                              <span className={`inline-flex items-center px-1.5 py-0.5 rounded border text-xs font-medium ${statusColour}`}>
                                {statusLabel}
                              </span>
                            </td>
                            <td className="py-2 pr-3 text-slate-500 max-w-xs truncate" title={log.subject}>{log.subject}</td>
                            <td className="py-2 text-slate-400 whitespace-nowrap">
                              {log.created_at ? new Date(log.created_at).toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'short' }) : '—'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  <p className="mt-3 text-xs text-amber-700 font-medium" data-testid="notif-isolation-hint">
                    Rows held by org setting are permanently skipped and will not be replayed if the setting is changed later.
                  </p>
                </div>
              ) : null}
            </div>
          )}
        </div>
      </div>
    </div>

      {/* Delete Plan confirmation modal — auditor only, before client input */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" data-testid="delete-plan-modal">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md mx-4 p-6">
            <div className="flex items-start gap-3 mb-4">
              <div className="bg-red-100 rounded-full p-2 shrink-0">
                <Trash2 className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-gray-900">Delete Action Plan?</h3>
                <p className="text-sm text-gray-500 mt-1">
                  This permanently removes the plan and all source-seeded context.
                  Only allowed before any client input has been saved.
                </p>
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 mt-2">
                  A permanent audit record of this deletion will be retained regardless.
                </p>
              </div>
            </div>
            <div className="mb-5">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Reason for deletion <span className="text-red-500">*</span>
              </label>
              <textarea
                className="w-full border rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-red-400"
                rows={3}
                placeholder="Describe why this plan is being deleted (min 10 characters)…"
                value={deleteReason}
                onChange={e => setDeleteReason(e.target.value)}
                data-testid="delete-plan-reason-input"
              />
              {deleteReason.length > 0 && deleteReason.trim().length < 10 && (
                <p className="text-xs text-red-500 mt-1">Reason must be at least 10 characters.</p>
              )}
            </div>
            <div className="flex justify-end gap-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { setShowDeleteModal(false); setDeleteReason(''); }}
                disabled={deleting}
                data-testid="delete-plan-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleDeletePlan}
                disabled={deleting || deleteReason.trim().length < 10}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="delete-plan-confirm-btn"
              >
                {deleting
                  ? <Loader2 className="w-3 h-3 animate-spin mr-1" />
                  : <Trash2 className="w-3 h-3 mr-1" />}
                Confirm Delete
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Items 2/9: Publish Confirmation Modal */}
      {showPublishModal && (
        <PublishConfirmModal
          preview={publishPreview}
          loading={publishPreviewLoading}
          publishing={publishing}
          onConfirm={handlePublishDraft}
          onCancel={() => { setShowPublishModal(false); setPublishPreview(null); }}
        />
      )}
    </>
  );
}

function PublishConfirmModal({ preview, loading, publishing, onConfirm, onCancel }) {
  if (!loading && !preview) return null;
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
      data-testid="publish-confirm-modal">
      <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full p-6 space-y-4">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Publish Action Plan</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              Review the details below before publishing.
              {preview && !preview.suppression_active ? ' Notifications will be sent immediately on confirm.' : ' Email notifications are currently suppressed — publishing will not send any email.'}
            </p>
          </div>
        </div>
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-gray-500 py-4">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading preview…
          </div>
        ) : (
          <div className="space-y-3">
            {/* Threshold + result context */}
            {(preview.result_pct !== null && preview.result_pct !== undefined) && (
              <div className="bg-gray-50 rounded-lg p-3 text-sm space-y-1">
                <p className="font-medium text-gray-700">Threshold basis</p>
                <p className="text-gray-600">
                  Result: <strong>{`${Number(preview.result_pct) > 0 ? '+' : ''}${Number(preview.result_pct).toFixed(2)}%`}</strong>
                  {preview.threshold_low != null && ` · Low: ${Number(preview.threshold_low) > 0 ? '+' : ''}${Number(preview.threshold_low).toFixed(2)}%`}
                  {preview.threshold_high != null && ` / High: ${Number(preview.threshold_high) > 0 ? '+' : ''}${Number(preview.threshold_high).toFixed(2)}%`}
                  {preview.excess_pp != null && (
                    <span className="ml-1 text-amber-700 font-medium">
                      ({Number(preview.excess_pp).toFixed(2)}% {preview.breach_direction} threshold)
                    </span>
                  )}
                </p>
              </div>
            )}
            {/* Suppression / recipients */}
            <div className={`rounded-lg p-3 text-sm ${preview.suppression_active ? 'bg-gray-100' : 'bg-amber-50 border border-amber-200'}`}>
              <p className="font-medium text-gray-700 mb-1">{preview.publish_warning}</p>
              {!preview.suppression_active && (preview.recipients || []).length > 0 && (
                <ul className="space-y-0.5 mt-1">
                  {preview.recipients.map((r, i) => (
                    <li key={i} className="text-xs text-gray-600">• {r.email}</li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" size="sm" onClick={onCancel} disabled={publishing} data-testid="publish-cancel-btn">
            Cancel — keep as draft
          </Button>
          <Button
            size="sm"
            onClick={onConfirm}
            disabled={publishing || loading}
            className="bg-amber-600 hover:bg-amber-700 text-white"
            data-testid="publish-confirm-btn"
          >
            {publishing ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
            Confirm &amp; Publish
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Prior AP review row — auditor sets verdict per CA (Item 7) ────────────────

function PriorCaReviewRow({ ca, planId, itemId, onSaved }) {
  const [assessment, setAssessment] = useState(ca.auditor_assessment || '');
  const [note, setNote]             = useState(ca.auditor_assessment_note || '');
  const [saving, setSaving]         = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await actionPlansService.updateCorrectiveAction(planId, itemId, ca.ca_id, {
        auditor_assessment:      assessment || null,
        auditor_assessment_note: note.trim() || null,
      });
      onSaved?.();
    } catch (e) {
      toast.error('Failed to save verdict: ' + (e?.response?.data?.detail || 'Error'));
    } finally {
      setSaving(false);
    }
  };

  const isSaved = assessment === ca.auditor_assessment && note === (ca.auditor_assessment_note || '');

  return (
    <div className="mt-2 border-t pt-2 text-xs space-y-1" data-testid={`prior-ca-review-${ca.ca_id}`}>
      <p className="text-gray-600"><span className="font-medium">Action:</span> {ca.description}</p>
      {ca.by_who && <p className="text-gray-500">By: {ca.by_who}{ca.by_when && ` · Due: ${ca.by_when}`}</p>}
      <div className="flex flex-wrap gap-2 items-center mt-1">
        <select
          value={assessment}
          onChange={e => setAssessment(e.target.value)}
          className="border rounded px-2 py-1 text-xs"
          data-testid={`prior-ca-assessment-${ca.ca_id}`}
        >
          <option value="">— Not assessed —</option>
          <option value="implemented">Implemented</option>
          <option value="carry_forward">Carry Forward</option>
        </select>
        <input
          type="text"
          value={note}
          onChange={e => setNote(e.target.value)}
          placeholder="Auditor comment (optional)…"
          className="border rounded px-2 py-1 text-xs flex-1 min-w-0"
        />
        <Button
          size="sm"
          variant={isSaved ? 'ghost' : 'outline'}
          onClick={handleSave}
          disabled={saving || isSaved}
          className="text-xs h-6 px-2"
          data-testid={`prior-ca-save-${ca.ca_id}`}
        >
          {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Save'}
        </Button>
      </div>
    </div>
  );
}
